// Shared low-poly ground-cover parts — the building blocks of grass, reed and
// blade tufts: a deterministic jitter stream (never Math.random, so a variant
// always rebuilds byte-identical geometry), an EXACT "tilt a y-aligned
// primitive toward a compass azimuth" rotation, and a two-segment arching
// blade. Ported from age-of-mcp's v2/groundcover-parts.ts: the pure geometry is
// kept verbatim, with the Part type taken from the library.

import * as THREE from "three";
import type { Part } from "../types";

/** Deterministic 0..1 hash stream keyed by (seed, n). */
export function h01(seed: number, n: number): number {
  const x = Math.sin(seed * 117.32 + n * 271.7 + 31.4) * 43758.5453;
  return x - Math.floor(x);
}

/** Linear blend between `a` and `b`. */
export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Unit direction of a y-aligned primitive tilted `tilt` radians from
 * vertical toward the compass azimuth `ang` (the +x/+z ground plane).
 */
export function dirAt(ang: number, tilt: number): [number, number, number] {
  return [
    Math.sin(tilt) * Math.cos(ang),
    Math.cos(tilt),
    Math.sin(tilt) * Math.sin(ang),
  ];
}

/**
 * The Part rotation that produces exactly `dirAt(ang, tilt)` for a y-aligned
 * primitive (Euler 'XYZ': yaw about Y after a roll about Z) — exact at any
 * tilt, unlike the small-angle [sin·t, 0, -cos·t] trick.
 */
export function tiltRot(ang: number, tilt: number): [number, number, number] {
  return [0, -ang, -tilt];
}

/** Spec for one arching two-segment grass/reed blade. */
export interface BladeArcSpec {
  /** Base position of the blade on the ground. */
  x: number; z: number;
  /** Compass azimuth the blade leans toward (radians). */
  ang: number;
  /** Tilt of the lower segment from vertical (radians). */
  lean: number;
  /** EXTRA tilt of the upper segment over the lower — the arch of the blade. */
  arch: number;
  /** Total blade length; the lower segment takes 55%, the upper 45%. */
  len: number;
  /** Blade width and thickness. */
  w: number; t: number;
  color: string;
}

/** A built arching blade plus where its tip ended up. */
export interface BladeArc {
  parts: Part[];
  /** World position of the blade tip — hang a seed head here. */
  tip: [number, number, number];
  /** Tilt of the upper segment from vertical — orient a seed head with it. */
  tipTilt: number;
}

/**
 * An arching blade built from two thin box segments: a lower segment tilted
 * by `lean` and an upper one bending a further `arch` over, so the blade
 * rises and nods instead of standing straight like a post.
 */
export function bladeArc(o: BladeArcSpec): BladeArc {
  const h1 = o.len * 0.55, h2 = o.len * 0.45;
  const d1 = dirAt(o.ang, o.lean);
  const d2 = dirAt(o.ang, o.lean + o.arch);
  const joint: [number, number, number] =
    [o.x + d1[0] * h1, d1[1] * h1, o.z + d1[2] * h1];
  const tip: [number, number, number] =
    [joint[0] + d2[0] * h2, joint[1] + d2[1] * h2, joint[2] + d2[2] * h2];
  return {
    parts: [
      { geometry: new THREE.BoxGeometry(o.w, h1, o.t), color: o.color,
        position: [o.x + d1[0] * h1 / 2, d1[1] * h1 / 2, o.z + d1[2] * h1 / 2],
        rotation: tiltRot(o.ang, o.lean) },
      { geometry: new THREE.BoxGeometry(o.w * 0.78, h2, o.t * 0.9), color: o.color,
        position: [joint[0] + d2[0] * h2 / 2, joint[1] + d2[1] * h2 / 2,
          joint[2] + d2[2] * h2 / 2],
        rotation: tiltRot(o.ang, o.lean + o.arch) },
    ],
    tip,
    tipTilt: o.lean + o.arch,
  };
}
